import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { 
  AlertTriangle, 
  CheckCircle,
  Info,
  ArrowLeft
} from 'lucide-react';
import EnterpriseLayout from '../components/layout/EnterpriseLayout';
import { CreditSummaryResponse, getAllCreditSummaries, submitPrepaidFee } from '../utils/creditService';
import TimezoneAwareTimestamp from '../components/common/TimezoneAwareTimestamp';
import HomeDrawCreditModal from '../components/modals/HomeDrawCreditModal';
import HomeRepayCreditModal from '../components/modals/HomeRepayCreditModal';
import Modal from '../components/common/Modal';
import { HomePayFeeModalViewList } from '../utils';
import { useCredit } from '../providers/CreditStatusProvider';
import { usePendingTransactions } from '../contexts/PendingTransactionContext';
import PendingTransactionIndicator from '../components/common/PendingTransactionIndicator';
import modalStyles from '../styles/common/ModalForm.module.scss';
import styles from '../styles/views/CreditLineDetail.module.scss';

const CreditLineDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setSelectedCreditLineId } = useCredit();
  const { startPolling, stopPolling, getPendingDataForCreditLine } = usePendingTransactions();
  const [creditLine, setCreditLine] = useState<CreditSummaryResponse & { id: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pay Fee Modal state
  const [currentPayFeeView, setCurrentPayFeeView] = useState(0);
  
  // Prepaid fee state
  const [isUsingPrepaidFee, setIsUsingPrepaidFee] = useState(false);
  const [prepaidFeeMessage, setPrepaidFeeMessage] = useState<string | null>(null);
  const [payFeeModalIsOpen, setPayFeeModalIsOpen] = useState(false);

  useEffect(() => {
    const fetchCreditLine = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const creditLineId = parseInt(id || '1');
        console.log("id, creditLineId", id, creditLineId);
        setSelectedCreditLineId(creditLineId);
        const response = await getAllCreditSummaries();
        
        // Find the credit line by matching available_balances.id
        const foundCreditLine = response.find(
          (creditLine) => creditLine.available_balances.id === creditLineId
        );
        
        if (!foundCreditLine) {
          throw new Error(`Credit line with ID ${creditLineId} not found`);
        }
        
        // Transform the response to include id from available_balances.id
        const creditLineWithId = {
          ...foundCreditLine,
          id: foundCreditLine.available_balances.id
        };
        
        setCreditLine(creditLineWithId);
      } catch (err) {
        console.error('Error fetching credit line:', err);
        setError(err instanceof Error ? err.message : 'Failed to load credit line details');
      } finally {
        setLoading(false);
      }
    };

    fetchCreditLine();
  }, [id, setSelectedCreditLineId]);

  // Start polling when component mounts and stop when unmounts
  useEffect(() => {
    startPolling();
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  // Pay Fee Modal handlers
  const handleTogglePayFeeModal = useCallback(() => {
    setPayFeeModalIsOpen(payFeeModalIsOpen => !payFeeModalIsOpen);
  }, []);

  const handleClosePayFeeModal = useCallback(() => {
    setPayFeeModalIsOpen(false);
    setCurrentPayFeeView(0);
  }, []);

  // Prepaid fee handler
  const handleUsePrepaidFee = useCallback(async () => {
    if (!creditLine || creditLine.credit_fees_owed <= 0) return;
    
    setIsUsingPrepaidFee(true);
    setPrepaidFeeMessage(null);
    
    try {
      const response = await submitPrepaidFee({ amount: creditLine.credit_fees_owed });
      setPrepaidFeeMessage(`Successfully used prepaid balance. ${response.message} Remaining balance: $${response.remaining_balance.toFixed(2)}`);
      
      // Refresh credit line data to show updated balances
      const creditLineId = parseInt(id!);
      const allCreditSummaries = await getAllCreditSummaries();
      const foundCreditLine = allCreditSummaries.find(cl => cl.available_balances.id === creditLineId);
      
      if (foundCreditLine) {
        const creditLineWithId = {
          ...foundCreditLine,
          id: foundCreditLine.available_balances.id
        };
        setCreditLine(creditLineWithId);
      }
    } catch (error) {
      console.error('Error using prepaid fee:', error);
      setPrepaidFeeMessage(
        error instanceof Error ? error.message : 'Failed to use prepaid fee balance'
      );
    } finally {
      setIsUsingPrepaidFee(false);
    }
  }, [creditLine, id]);

  const handlePayFeeBack = () => {
    setCurrentPayFeeView(0);
  };

  if (loading) {
    return (
      <EnterpriseLayout 
        pageTitle="Credit Line Details"
        pageDescription="Loading your credit line information and current balances."
      >
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="64" strokeDashoffset="0">
                <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="1s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
          <p>Loading credit line details...</p>
        </div>
      </EnterpriseLayout>
    );
  }

  if (error) {
    return (
      <EnterpriseLayout 
        pageTitle="Error Loading Credit Line"
        pageDescription="There was a problem loading your credit line details. Please try again."
      >
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>
            <AlertTriangle size={48} />
          </div>
          <h2>Unable to Load Credit Line</h2>
          <p>{error}</p>
          <div className={styles.errorActions}>
            <button 
              className={styles.retryButton}
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
            <button 
              className={styles.backButton}
              onClick={() => navigate('/credit-lines')}
            >
              Back to Credit Lines
            </button>
          </div>
        </div>
      </EnterpriseLayout>
    );
  }

  if (!creditLine) {
    return (
      <EnterpriseLayout 
        pageTitle="Credit Line Not Found"
        pageDescription="The requested credit line could not be found in your account."
      >
        <div className={styles.notFound}>
          <h2>Credit Line Not Found</h2>
          <p>The credit line you're looking for doesn't exist or has been removed.</p>
          <button 
            className={styles.backButton}
            onClick={() => navigate('/credit-lines')}
          >
            Back to Credit Lines
          </button>
        </div>
      </EnterpriseLayout>
    );
  }

  const utilizationPercent = (creditLine.available_balances.used / creditLine.available_balances.balance) * 100;
  const transactionPercent = 0; // Mock value - transactions used out of limit
  const hasOverdueFees = creditLine.credit_fees_owed > 0 && creditLine.days_overdue > 0;

  const formatAmount = (amount: number, context: 'balance' | 'fee' = 'balance') => {
    const decimals = context === 'fee' ? 4 : 2;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(amount);
  };

  const getMonthsRemaining = () => {
    const maturityDate = new Date(creditLine.maturity_date);
    const now = new Date();
    const monthsDiff = Math.ceil((maturityDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30));
    return Math.max(0, monthsDiff);
  };

  // Get pending transaction data for this credit line
  const pendingData = creditLine ? getPendingDataForCreditLine(creditLine.id) : null;

  return (
    <EnterpriseLayout 
      pageTitle="Credit Line Details"
      pageDescription="View detailed information about your credit line including balances, fees, transactions, and payment history."
    >
      <div className={clsx(styles.creditLineDetail, 'credit-line-detail')}>
        {/* Navigation */}
        <div className={styles.navigation}>
          <button 
            className={styles.backButton}
            onClick={() => navigate('/credit-lines')}
          >
            <ArrowLeft className={styles.backIcon} />
            Back to Credit Lines
          </button>
        </div>

        {/* Credit Line Header */}
        <div className={styles.creditLineHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.creditLineTitle}>
              <div className={styles.lineLabel}>{creditLine.label}</div>
              <div className={styles.commitmentInfo}>
                <div className={styles.commitmentLabel}>Credit Limit</div>
                <div className={styles.commitmentAmount}>
                  <span className={styles.currency}>{creditLine.available_balances.currency}</span>
                  <span className={styles.amount}>{formatAmount(creditLine.maximum_commitment)}</span>
                </div>
              </div>
            </div>
            <div className={styles.headerMeta}>
              <span className={styles.currencyMeta}>
                {creditLine.available_balances.currency} • {creditLine.network}
              </span>
              <div className={`${styles.statusBadge} ${styles[creditLine.status.toLowerCase()]}`}>
                <div className={styles.statusDot}></div>
                <span>{creditLine.status.toUpperCase()}</span>
              </div>
            </div>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.expiryInfo}>
              <div className={styles.expiryLabel}>Expires</div>
              <TimezoneAwareTimestamp 
                dateString={creditLine.maturity_date} 
                format="full"
                className={styles.expiryDate}
              />
              <div className={styles.monthsLeft}>{getMonthsRemaining()} months left</div>
            </div>
          </div>
        </div>

        {/* Main Layout Grid */}
        <div className={styles.mainLayout}>
          {/* Left Column */}
          <div className={styles.leftColumn}>
            {/* Credit Balance Section */}
            <div className={styles.section}>

              {/* Balance Overview */}
              <div className={styles.balanceOverview}>
                <div className={styles.balanceItem}>
                  <div className={styles.balanceLabel}>Available Balance</div>
                  <div className={styles.balanceAmount}>{formatAmount(creditLine.available_balances.available_credit_to_request)}</div>
                </div>
                <div className={styles.balanceDivider}></div>
                <div className={styles.balanceItem}>
                  <div className={styles.balanceLabel}>Credit Outstanding</div>
                  <div className={styles.balanceAmount}>{formatAmount(creditLine.available_balances.credit_balance - creditLine.available_balances.pending_repayments)}</div>
                </div>

                {/* Pending Repayments */}
                {creditLine.available_balances.pending_repayments > 0 && (
                  <div className={styles.pendingRepayments}>
                    <div className={styles.pendingIcon}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="64" strokeDashoffset="0">
                          <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="2s" repeatCount="indefinite"/>
                        </circle>
                      </svg>
                    </div>
                    <span className={styles.pendingText}>Pending repayments: {formatAmount(creditLine.available_balances.pending_repayments)}</span>
                  </div>
                )}
              </div>
              {/* Usage Summary */}
              <div className={styles.usageSummary}>
                {formatAmount(creditLine.available_balances.credit_balance)} used of {formatAmount(creditLine.maximum_commitment)} total limit
                <div className={styles.progressBar}>
                  <div 
                    className={clsx(styles.progress, utilizationPercent > 80 && styles.high)}
                    style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                  />
                </div>
              </div>

              <div className={styles.actionButtons}>
                <div className={styles.actionButtonGroup}>
                  <HomeDrawCreditModal creditLine={creditLine.available_balances} />
                  {pendingData && (
                    <PendingTransactionIndicator
                      pending_repayments={pendingData.pending_repayments}
                      pending_fee_repayments={pendingData.pending_fee_repayments}
                      pending_withdraws={pendingData.pending_withdraws}
                      type="withdrawal"
                      className={styles.compactIndicator}
                    />
                  )}
                </div>
                
                <div className={styles.actionButtonGroup}>
                  <HomeRepayCreditModal creditLine={creditLine.available_balances} />
                  {pendingData && (
                    <PendingTransactionIndicator
                      pending_repayments={pendingData.pending_repayments}
                      pending_fee_repayments={pendingData.pending_fee_repayments}
                      pending_withdraws={pendingData.pending_withdraws}
                      type="repayment"
                      className={styles.compactIndicator}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Usage Overview Section */}
            <div className={styles.section}>
              <h2>Usage Overview</h2>
              
              <div className={styles.usageStats}>
                <div className={styles.usageStat}>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{(creditLine.recycled_capital / creditLine.maximum_commitment).toFixed(2)} x</div>
                    <div className={styles.statLabel}>Recyled Capital</div>
                    <div className={styles.statProgress}>
                      <div 
                        className={styles.statProgressBar}
                        style={{ width: `${transactionPercent}%` }}
                      />
                    </div>
                  </div>
                </div>
                
                <div className={styles.usageStat}>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{getMonthsRemaining()}</div>
                    <div className={styles.statLabel}>months remaining</div>
                    <TimezoneAwareTimestamp 
                      dateString={creditLine.maturity_date} 
                      format="short"
                      className={styles.statSubLabel}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.recentActivity}>
                <div className={styles.activityCard}>
                  <Info className={styles.activityIcon} />
                  <div className={styles.activityContent}>
                    <div className={styles.activityTitle}>Recent Activity</div>
                    <div className={styles.activityText}>
                      Last transaction: Dec 24, 2024
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className={styles.rightColumn}>
            {/* Fees & Payments Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>Fees</h2>
                <div className={clsx(styles.feesBadge, hasOverdueFees && styles.overdue)}>
                  {hasOverdueFees ? 'Payment Due' : 'Current'}
                </div>
              </div>

              {creditLine.credit_fees_owed > 0 ? (
                hasOverdueFees ? (
                  <div className={clsx(styles.feesAlert, styles.overdue)}>
                    <div className={styles.feesAlertContent}>
                      <div className={styles.feesAlertLeft}>
                        <div>
                          <div className={styles.feesAmount}>
                            {formatAmount(creditLine.credit_fees_owed, 'fee')} owed
                          </div>
                          <div className={styles.feesNote}>
                            Payment overdue by {creditLine.days_overdue} days
                          </div>
                          {/* Show prepaid fee info if available */}
                          {(creditLine.borrower_prepaid_fee_balance || 0) >= creditLine.credit_fees_owed && (
                            <div className={styles.prepaidFeeInfo}>
                              You have a prepaid fee amount of ${(creditLine.borrower_prepaid_fee_balance || 0).toFixed(2)} available
                            </div>
                          )}
                          {/* Show success/error message */}
                          {prepaidFeeMessage && (
                            <div className={clsx(styles.prepaidFeeMessage, prepaidFeeMessage.includes('Successfully') ? styles.success : styles.error)}>
                              {prepaidFeeMessage}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles.feesButtonGroup}>
                        {/* Show Use Prepaid button if prepaid balance is sufficient, otherwise show Pay Now */}
                        {(creditLine.borrower_prepaid_fee_balance || 0) >= creditLine.credit_fees_owed ? (
                          <button 
                            className={styles.usePrepaidButton}
                            onClick={handleUsePrepaidFee}
                            disabled={creditLine.credit_fees_owed <= 0 || isUsingPrepaidFee}
                          >
                            {isUsingPrepaidFee ? 'Processing...' : 'Use Prepaid'}
                          </button>
                        ) : (
                          <button 
                            className={styles.payFeesButton}
                            onClick={handleTogglePayFeeModal}
                            disabled={creditLine.credit_fees_owed <= 0}
                          >
                            Pay Now
                          </button>
                        )}
                        {pendingData && (
                          <PendingTransactionIndicator
                            pending_fee_repayments={pendingData.pending_fee_repayments}
                            type="fee_payment"
                            className={styles.compactIndicator}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.feesNeutral}>
                    <div className={styles.feesNeutralContent}>
                      <div className={styles.feesNeutralLeft}>
                        <div>
                          <div className={styles.feesAmount}>
                            {formatAmount(creditLine.credit_fees_owed, 'fee')} due
                          </div>
                          <div className={styles.feesNote}>
                            Based on this month's usage
                          </div>
                        </div>
                      </div>
                      <div className={styles.feesButtonGroup}>
                        <button 
                          className={styles.payFeesButton}
                          onClick={handleTogglePayFeeModal}
                          disabled={creditLine.credit_fees_owed <= 0}
                        >
                          Pay Fees
                        </button>
                        {pendingData && (
                          <PendingTransactionIndicator
                            pending_fee_repayments={pendingData.pending_fee_repayments}
                            type="fee_payment"
                            className={styles.compactIndicator}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div className={styles.feesGood}>
                  <CheckCircle className={styles.checkIcon} />
                  <span>No outstanding fees</span>
                </div>
              )}

              <div className={styles.paymentInfo}>
                <div className={styles.paymentInfoItem}>
                  <span className={styles.paymentLabel}>Payment Schedule</span>
                  <span className={styles.paymentValue}>{creditLine.credit_fee_payment_period}</span>
                </div>
                <div className={styles.paymentInfoItem}>
                  <span className={styles.paymentLabel}>Fee Due Date</span>
                  <span className={styles.paymentValue}>
                    {creditLine.next_payment_due || 'No payment due'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Pay Fee Modal */}
      {creditLine && (
        <Modal
          extraClassName={clsx(modalStyles.cModalForm, "c-modal-form", "c-home-pay-fee-modal", `view-${currentPayFeeView}`)}
          isModalOpen={payFeeModalIsOpen}
          onCloseModal={handleClosePayFeeModal}
          hideModalHeader
          modalSize={currentPayFeeView === 0 ? "md" : "sm"}
        >
          {HomePayFeeModalViewList({ 
            onCloseModal: handleClosePayFeeModal, 
            setCurrentView: setCurrentPayFeeView, 
            handleBack: handlePayFeeBack,
            creditLine: creditLine.available_balances,
          })[currentPayFeeView]?.component}
        </Modal>
      )}
    </EnterpriseLayout>
  );
};

export default CreditLineDetail;
